import { useNavigate } from 'react-router-dom';
import React, { useState, useRef, useEffect } from 'react';
import './instructions.css';
import { getCandidateDataStatus, getCandidateSignature, saveCandidateSignature } from '../../../services/candidate/api';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getS3key } from '../../../services/employee/api';

const Instructions = () => {
  const navigate = useNavigate();

  const [checkboxes, setCheckboxes] = useState({ consent1: false, consent2: false });
  const [signature, setSignature] = useState(null); // data URL or server URL 
  const [signatureSource, setSignatureSource] = useState(null);
  const [typedSignature, setTypedSignature] = useState('');
  const [tab, setTab] = useState('draw');
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isSignatureSubmitted, setIsSignatureSubmitted] = useState(false);
  const [showAadhaarPopup, setShowAadhaarPopup] = useState(false);
  const [otpStage, setOtpStage] = useState(false);
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [aadhaarOtp, setAadhaarOtp] = useState('');
  const [clientId, setClientId] = useState(null);
  const [aadhaarError, setAadhaarError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(600);
  const [canResend, setCanResend] = useState(false);


  const dataURLtoFile = (dataUrl, filename = "signature.png") => {
    if (!dataUrl || !dataUrl.startsWith('data:')) return null;
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) return null;
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };


  useEffect(() => {
    const dashboardWrapper = document.querySelector('.dashboard-container');
    if (dashboardWrapper) {
      dashboardWrapper.style.backgroundImage = 'none';
      dashboardWrapper.style.backgroundColor = '#f4f4f4';
    }
    document.body.classList.add('no-background');
    return () => {
      if (dashboardWrapper) {
        dashboardWrapper.style.backgroundImage = '';
        dashboardWrapper.style.backgroundColor = '';
      }
      document.body.classList.remove('no-background');
    };
  }, []);


  useEffect(() => {
    let countdown;
    if (otpStage) {
      countdown = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            clearInterval(countdown);
            setCanResend(true);
            return 0;
          }
          if (prev <= 480) setCanResend(true);
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(countdown);
  }, [otpStage]);

  useEffect(() => {
    const fetchSignature = async () => {
      try {
        const response = await getCandidateSignature();
        const url = response?.data?.signatureFileUrl;
        if (url) {
          await loadSignatureFromS3(url);
          setIsSignatureSubmitted(true);
        }
      } catch (error) {
        console.error('Error fetching signature:', error);
      }
    };
    fetchSignature();
  }, []);

  useEffect(() => {
    if (isSignatureSubmitted) return;

    setSignature(null);
    setSignatureSource(null);
    setTypedSignature('');
    setShowPreview(false);

    if (tab === 'draw') {
      initCanvas(canvasRef.current);
    } else if (tab === 'upload') {
      const input = document.querySelector('.instructions-upload-input');
      if (input) input.value = '';
    }
  }, [tab, isSignatureSubmitted]);

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const startTimer = () => {
    setTimer(600);
    setCanResend(false);
  };

  const initCanvas = (canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const parent = canvas.parentElement;
    const cssWidth = parent ? parent.offsetWidth : 800;
    const cssHeight = cssWidth * 0.25;

    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 1.5;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000000";

    setSignature(canvas.toDataURL("image/png"));
    setSignatureSource('draw');
  };

  useEffect(() => {
    if (canvasRef.current) initCanvas(canvasRef.current);
  }, []);

  const handleCheckboxChange = (e) => {
    setCheckboxes(prev => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSignature(null);
      setSignatureSource(null);
      setShowPreview(false);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5MB or smaller");
      e.target.value = '';
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload a valid image");
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSignature(reader.result);
      setSignatureSource('upload');
      setShowPreview(true);
    };
    reader.readAsDataURL(file);
  };

  const handleTypedSignature = (e) => {
    const text = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 20);
    setTypedSignature(text);
    if (!text.trim()) {
      setSignature(null);
      setSignatureSource(null);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = 400 * dpr;
    canvas.height = 150 * dpr;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, 400, 150);
    ctx.font = '40px cursive';
    ctx.fillStyle = 'black';
    ctx.fillText(text, 20, 90);
    setSignature(canvas.toDataURL('image/png'));
    setSignatureSource('type');
  };

  const getEventPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (800 / rect.width),
      y: (clientY - rect.top) * (200 / rect.height)
    };
  };
  const startDrawing = (e) => {
    if (tab !== 'draw' || !canvasRef.current) return;
    e.preventDefault();
    isDrawing.current = true;
    const { x, y } = getEventPosition(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineWidth = 1.5;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000000";

    ctx.beginPath();
    ctx.moveTo(x, y);
  };


  const draw = (e) => {
    if (!isDrawing.current || tab !== 'draw') return;
    e.preventDefault();
    const { x, y } = getEventPosition(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDrawing = () => {
    if (!isDrawing.current || tab !== 'draw') return;
    isDrawing.current = false;
    setSignature(canvasRef.current.toDataURL('image/png'));
    setSignatureSource('draw');
  };

  const clearCanvas = () => {
    setSignature(null);
    setSignatureSource(null);
    setTypedSignature('');
    setShowPreview(false);
    if (tab === 'draw') initCanvas(canvasRef.current);
    if (tab === 'upload') {
      const input = document.querySelector('.instructions-upload-input');
      if (input) input.value = '';
    }
  };

  const isCanvasBlank = () => {
    if (!canvasRef.current) return true;
    const data = canvasRef.current.getContext('2d').getImageData(0, 0, canvasRef.current.width, canvasRef.current.height).data;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255) return false;
    }
    return true;
  };

  const handleSignClick = () => {
    if (isSignatureSubmitted) {
      toast.info("Signature already submitted.");
      return;
    }

    if (tab === 'draw') {
      if (isCanvasBlank() || !signature || signatureSource !== 'draw') {
        toast.error("Please draw your signature.");
        return;
      }
    } else if (tab === 'type') {
      if (!typedSignature.trim() || !signature || signatureSource !== 'type') {
        toast.error("Please type your name.");
        return;
      }
    } else if (tab === 'upload') {
      if (!signature || signatureSource !== 'upload') {
        toast.error("Please upload a valid signature image.");
        return;
      }
    }

    setShowConfirmPopup(true);
  };

  const handleConfirmSign = async () => {
    setShowConfirmPopup(false);
    try {
      const file = dataURLtoFile(signature);
      if (!file) {
        toast.error("Invalid signature. Please try again.");
        return;
      }

      await saveCandidateSignature(file, tab);
      toast.success("Signature saved successfully!");

      const res = await getCandidateSignature();
      const uploadedUrl = res?.data?.signatureFileUrl;
      if (uploadedUrl) {
        loadSignatureFromS3(uploadedUrl);
      }
      setIsSignatureSubmitted(true);

    } catch (error) {
      console.error("Error saving signature:", error);
      toast.error(error?.response?.data?.message || "Failed to save signature. Please try again.");
    }
  };

  const handleAadhaarSubmit = async () => {
    setAadhaarError('');
    setIsLoading(true);
    const cleanAadhaar = aadhaarNumber.replace(/\s/g, '');

    if (!/^\d{12}$/.test(cleanAadhaar)) {
      setAadhaarError('Please enter a valid 12-digit Aadhaar number.');
      setIsLoading(false);
      return;
    }

   try {
      const response = await fetch('https://kyc-api.surepass.app/api/v1/aadhaar-v2/generate-otp', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmcmVzaCI6ZmFsc2UsImlhdCI6MTc1MTA4NzkxNywianRpIjoiOTk0MWMzMGUtNGY4Ny00NTNkLWE0OTEtODNkNmZiNDE0OGEzIiwidHlwZSI6ImFjY2VzcyIsImlkZW50aXR5IjoiZGV2LnVzZXJuYW1lXzJ4czhtMzRoZDZ1ZHFheWh2aG9uYzQxdmhzeEBzdXJlcGFzcy5pbyIsIm5iZiI6MTc1MTA4NzkxNywiZXhwIjoyMzgxODA3OTE3LCJlbWFpbCI6InVzZXJuYW1lXzJ4czhtMzRoZDZ1ZHFheWh2aG9uYzQxdmhzeEBzdXJlcGFzcy5pbyIsInRlbmFudF9pZCI6Im1haW4iLCJ1c2VyX2NsYWltcyI6eyJzY29wZXMiOlsidXNlciJdfX0.HJHPVrHRTVD0wwXiiI226F7A7r_fIAjMvVaChKoc6W8',
          'Content-Type': 'application/json',
          'Tenant-Id': 'main'
        },
        body: JSON.stringify({ id_number: cleanAadhaar, tenant_id: 'main' })
      });

      const data = await response.json();


      if (data.success && data.data?.client_id) {
        toast.success("OTP sent successfully!");
        setClientId(data.data.client_id);
        startTimer();
        setOtpStage(true);
      } else {
        setAadhaarError(data.error?.message || 'Failed to send OTP.');
      }
    } catch (error) {
      setAadhaarError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (!aadhaarOtp || aadhaarOtp.length !== 6) {
      setAadhaarError('Please enter a valid 6-digit OTP.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('https://kyc-api.surepass.app/api/v1/aadhaar-v2/submit-otp', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmcmVzaCI6ZmFsc2UsImlhdCI6MTc1MTA4NzkxNywianRpIjoiOTk0MWMzMGUtNGY4Ny00NTNkLWE0OTEtODNkNmZiNDE0OGEzIiwidHlwZSI6ImFjY2VzcyIsImlkZW50aXR5IjoiZGV2LnVzZXJuYW1lXzJ4czhtMzRoZDZ1ZHFheWh2aG9uYzQxdmhzeEBzdXJlcGFzcy5pbyIsIm5iZiI6MTc1MTA4NzkxNywiZXhwIjoyMzgxODA3OTE3LCJlbWFpbCI6InVzZXJuYW1lXzJ4czhtMzRoZDZ1ZHFheWh2aG9uYzQxdmhzeEBzdXJlcGFzcy5pbyIsInRlbmFudF9pZCI6Im1haW4iLCJ1c2VyX2NsYWltcyI6eyJzY29wZXMiOlsidXNlciJdfX0.HJHPVrHRTVD0wwXiiI226F7A7r_fIAjMvVaChKoc6W8',
          'Content-Type': 'application/json',
          'Tenant-Id': 'main'
        },
        body: JSON.stringify({ client_id: clientId, otp: aadhaarOtp })
      });

      const data = await response.json();

      if (data.success && data.data) {
        const { full_name, aadhaar_number, dob, gender, care_of, profile_image, address, zip } = data.data;
        localStorage.setItem('personal_response', JSON.stringify({ full_name, aadhaar_number, dob, gender, care_of, profile_image }));
        localStorage.setItem('address_response', JSON.stringify({ address, zip }));
        toast.success("Aadhaar verified successfully!");
        navigate('/candidate/personal-info');
      } else {
        setAadhaarError(data.error?.message || 'Invalid OTP.');
      }
    } catch (error) {
      setAadhaarError('Verification failed. Try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAndContinue = async () => {
    const aadhaarDataFetched = localStorage.getItem('aadhaarDataFetched');
    const personalDataFetched = localStorage.getItem('personalData');
    const addressDataFetched = localStorage.getItem('address_response');

    try {
      const response = await getCandidateDataStatus();
      if (response?.data?.dataComplete) {
        navigate('/candidate/personal-info');
      } else {
        if (!aadhaarDataFetched & !personalDataFetched & !addressDataFetched) {
          navigate('/candidate/digilocker-router')
        } else {
          navigate('/candidate/personal-info');
        }
      }
    } catch (error) {
      toast.error("Failed to proceed. Please try again.");
    }
  };

  const loadSignatureFromS3 = async (fileKey) => {
  try {
    if (!fileKey) return;

    let key = fileKey;

    // remove full S3 url if present
    if (key.includes(".com/")) {
      key = key.split(".com/")[1];
    }

    key = decodeURIComponent(key);

    const response = await getS3key(key);

    const blobUrl = URL.createObjectURL(response.data);

    setSignature(blobUrl);

  } catch (error) {
    console.error("Error loading signature:", error);
  }
};

useEffect(() => {
  return () => {
    if (signature?.startsWith("blob:")) {
      URL.revokeObjectURL(signature);
    }
  };
}, [signature]);

  return (
    <div className="instructions-wrapper">
      <div className="instructions-form-container">
        {/* Instructions */}
        <h2 style={{ fontWeight: 'bold' }}>Instructions</h2>
        <div className="instructions-block">
          <p>1. This form is divided into the following sections:<br />- Personal Details<br />- Address Details<br />- Education Details<br />- Employment Details</p>
          <p>2. Fields marked with * are mandatory.</p>
          <p>3. Some details will be shown in the review section and can be edited there. Others require email communication to modify.</p>
          <p>4. Candidate personal information and permanent address will be automatically retrieved from Digi Locker.</p>
          <p>5. Provide only correct and legitimate information. Misrepresentation can affect your offer.</p>
          <p>6. Review all details carefully before submitting.</p>
          <p className="instructions-note">Note: Once submitted, data cannot be changed on the website.</p>
          <p className="instructions-note">Note: You can log in for 5 days from the BGC invitation date.</p>
        </div>

        <form>
          <h3 style={{ fontWeight: 'bold' }}>Accept Terms and Conditions</h3>
          <div className="instructions-checkbox-group">
            <input type="checkbox" id="consent1" name="consent1" checked={checkboxes.consent1} onChange={handleCheckboxChange} />
            <label htmlFor="consent1">I agree to share my personal, address, education, employment info with the organization for background check verification.</label>
          </div>
          <div className="instructions-checkbox-group">
            <input type="checkbox" id="consent2" name="consent2" checked={checkboxes.consent2} onChange={handleCheckboxChange} />
            <label htmlFor="consent2">I agree that the organization may share my information with third parties if necessary.</label>
          </div>

          {(checkboxes.consent1 && checkboxes.consent2) && (
            <div className="instructions-signature-section">
              <h3>Provide Signature</h3>

              {!isSignatureSubmitted && (
                <div
                  className="signature-instruction-text"
                  style={{
                    textAlign: 'left',
                    color: '#666',
                    fontSize: '14px',
                    marginBottom: '15px',
                    lineHeight: '1.6'
                  }}
                >
                  <p style={{ marginBottom: '8px', fontWeight: '500' }}>
                    Please provide your signature using one of the following methods:
                  </p>

                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    <li><strong>Draw</strong> your signature directly on the canvas</li>
                    <li><strong>Type</strong> your name to generate a signature</li>
                    <li><strong>Upload</strong> a clear image file from your device</li>
                  </ul>
                </div>
              )}
              <div className="signature-center-wrapper">
                <div className="instructions-signature-section-container">

                  {isSignatureSubmitted && signature ? (
                    <div className="instructions-signature-preview">
                      <p><strong>Your Submitted Signature:</strong></p>
                      <img src={signature} alt="Saved Signature" style={{ maxWidth: '100%', border: '1px solid #ccc', margin: '10px 0' }} />
                      <p style={{ color: 'green', fontWeight: 'bold', textAlign: 'center' }}>Signature submitted successfully. Cannot edit.</p>
                    </div>
                  ) : (
                    <>
                      <div className="instructions-tabs" style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                        <button type="button" className={`instructions-tab-btn ${tab === 'draw' ? 'active' : ''}`} onClick={() => setTab('draw')}>Draw</button>
                        <button type="button" className={`instructions-tab-btn ${tab === 'type' ? 'active' : ''}`} onClick={() => setTab('type')}>Type</button>
                        <button type="button" className={`instructions-tab-btn ${tab === 'upload' ? 'active' : ''}`} onClick={() => setTab('upload')}>Upload</button>
                      </div>

                      {tab === 'draw' && (
                        <div className="instructions-draw-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <canvas
                            ref={canvasRef}
                            width={837}
                            height={250}
                            className="instructions-signature-canvas"
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={endDrawing}
                            onMouseLeave={endDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={endDrawing}
                          />

                        </div>
                      )}

                      {tab === 'type' && (
                        <input
                          type="text"
                          value={typedSignature}
                          placeholder="Type your full name"
                          onChange={handleTypedSignature}
                          className="instructions-type-input"
                          style={{ width: '100%', maxWidth: '500px', margin: '10px auto', display: 'block', padding: '12px', fontSize: '18px' }}
                        />
                      )}

                      {tab === 'upload' && (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="instructions-upload-input"
                            style={{ width: '100%', maxWidth: '500px', margin: '10px auto', display: 'block' }}
                          />
                          <p style={{
                            fontSize: '14px',
                            color: '#1976d2',
                            textAlign: 'center',
                            margin: '12px 0 20px',
                            fontWeight: '500',
                          }}>
                            Please upload a clear image of your signature (Max 5 MB)
                          </p>
                        </>
                      )}

                      {signature && (
                        <div className='preview-clear-button'>
                          <div className='preview-clear-button-data'>
                            {!showPreview && (
                              <button type="button" className="instructions-preview-btn" onClick={() => setShowPreview(true)}>
                                Preview
                              </button>
                            )}
                            {showPreview && (
                              <div className="instructions-signature-preview">
                                <p><strong>Preview:</strong></p>
                                <img src={signature} alt="Signature Preview" style={{ maxWidth: '100%', border: '1px solid #ccc' }} />
                              </div>
                            )}
                            <button type="button" className="instructions-clear-btn" onClick={clearCanvas}>Clear</button>
                          </div>
                        </div>
                      )}

                      <button type="button" className="instructions-sign-btn" onClick={handleSignClick}>
                        Sign
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="instructions-form-actions">
                <button
                  type="button"
                  disabled={!(checkboxes.consent1 && checkboxes.consent2 && isSignatureSubmitted)}
                  className="instructions-submit-btn"
                  onClick={handleSaveAndContinue}
                >
                  Save & Continue
                </button>
              </div>
            </div>
          )}

          {/* Aadhaar Popup */}
          {showAadhaarPopup && (
            <div className="popup-overlay">
              <div className="popup">
                <h4 className='popup-h4'>Aadhaar Verification</h4>
                <div className="aadhar-container">
                  <label>Aadhaar Number</label>
                  <input
                    type="text"
                    value={aadhaarNumber}
                    disabled={otpStage}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '').slice(0, 12);
                      const formatted = value.replace(/(.{4})/g, '$1 ').trim();
                      setAadhaarNumber(formatted);
                    }}
                    maxLength={14}
                    placeholder="XXXX XXXX XXXX"
                    className="aadhaar-input"
                  />
                  {aadhaarError && <p style={{ color: 'red', marginTop: '10px' }}>{aadhaarError}</p>}
                </div>

                {otpStage && (
                  <div className="otp-container">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <label>OTP</label>
                      <span style={{ fontSize: "14px", color: "gray" }}>
                        {timer > 0 ? `Time left: ${formatTime(timer)}` : "Expired"}
                      </span>
                    </div>
                    <input
                      type="text"
                      maxLength={6}
                      value={aadhaarOtp}
                      onChange={(e) => setAadhaarOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      className="aadhaar-input"
                    />
                    <button
                      type="button"
                      className="resend-otp-btn"
                      onClick={handleAadhaarSubmit}
                      disabled={!canResend || isLoading}
                    >
                      Resend OTP
                    </button>
                  </div>
                )}

                <div className="popup-actions">
                  <button onClick={() => {
                    setShowAadhaarPopup(false);
                    setOtpStage(false);
                    setAadhaarError('');
                    setAadhaarOtp('');
                  }}>Cancel</button>
                  {!otpStage ? (
                    <button onClick={handleAadhaarSubmit} disabled={isLoading}>
                      {isLoading ? 'Sending...' : 'Send OTP'}
                    </button>
                  ) : (
                    <button onClick={handleOtpSubmit} disabled={isLoading}>
                      {isLoading ? 'Verifying...' : 'Verify & Continue'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Popup */}
          {showConfirmPopup && (
            <div className="popup-overlay">
              <div className="popup-content">
                <h3>Confirmation</h3>
                <p className='popup-title'>Click OK to submit your signature</p>
                <div className="popup-modal-content">
                  <button type="button" className='modal-btn no' onClick={() => setShowConfirmPopup(false)}>Cancel</button>
                  <button type="button" className='modal-btn confirm' onClick={handleConfirmSign}>OK</button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Instructions;